import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Permissions are decided by the backend (/auth/me). The UI only uses them to hide what the
// user cannot do; every API call is still authorised server-side.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const [ready, setReady] = useState(false);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(false); setReady(true); return; }
    refreshUser()
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(false); })
      .finally(() => setReady(true));
  }, [refreshUser]);

  useEffect(() => {
    if (!ready || !user) return undefined;
    const refreshOnFocus = () => refreshUser().catch(() => {});
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, [ready, user, refreshUser]);

  // Store a token issued by login / change-password and adopt the returned user.
  const setSession = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser({ ...data.user, password_change_required: !!data.password_change_required,
              session_expires_at: data.session_expires_at });
    return data.user;
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return setSession(data);
  };

  const logout = useCallback(async () => {
    try { if (localStorage.getItem(TOKEN_KEY)) await api.post("/auth/logout"); } catch { /* already invalid */ }
    localStorage.removeItem(TOKEN_KEY);
    // Nothing user-scoped survives a sign-out in this browser tab (pending Zerodha connect requests).
    try {
      Object.keys(sessionStorage).filter((k) => k.startsWith("gz_auth_")).forEach((k) => sessionStorage.removeItem(k));
    } catch { /* storage unavailable */ }
    setUser(false);
    window.location.href = "/login";
  }, []);

  const can = useCallback((permission) => !!user && (user.permissions || []).includes(permission), [user]);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, setSession, refreshUser, can }}>
      {children}
    </AuthContext.Provider>
  );
}
